"use client";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
// import { Listbox, Transition } from "@headlessui/react";

interface CSelectOption {
  label: React.ReactNode;
  selectedLabel?: React.ReactNode;
  value: string | number;
  disabled?: boolean; // NEW - to disable specific options
}

interface CSelectProps {
  name?: string;
  id?: string;
  label?: React.ReactNode;
  placeholder?: string;
  className?: string;
  value?: string | number;
  defaultValue?: string | number;
  options: CSelectOption[];
  onChange?: (value: string | number) => void;
  disabled?: boolean;
  errorQuery?: boolean;
  errorMessage?: string;
  required?: boolean;
}

const CSelect: React.FC<
  CSelectProps & {
    clearable?: boolean;
    searchable?: boolean;
    width?: string;
    style?: React.CSSProperties;
    maxHeight?: string;
  }
> = ({
  name,
  id,
  label,
  placeholder = "Select an option",
  className = "",
  value,
  defaultValue,
  options,
  onChange,
  disabled = false,
  errorQuery = false,
  errorMessage = "",
  required = false,
  clearable = false,
  searchable = false,
  width,
  style,
  maxHeight = "max-h-60",
}) => {
  const inputId = id || name || "cselect";
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [search, setSearch] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [mounted, setMounted] = useState(false);

  // Ensure component is mounted (for portal)
  useEffect(() => {
    setMounted(true);
  }, []);

  const uniqueOptions = Array.from(
    new Map(options.map((opt) => [opt.value, opt])).values(),
  );
  const selectedValue = value ?? defaultValue ?? "";
  const selectedOption =
    uniqueOptions.find((opt) => opt.value === selectedValue) || null;
  const filteredOptions =
    searchable && search
      ? uniqueOptions.filter((opt) => {
          if (typeof opt.label === "string") {
            return opt.label.toLowerCase().includes(search.toLowerCase());
          }
          // If label is JSX, fallback to value string
          return String(opt.value).toLowerCase().includes(search.toLowerCase());
        })
      : uniqueOptions;

  // Update dropdown position when opened
  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft =
        window.pageXOffset || document.documentElement.scrollLeft;

      setDropdownPosition({
        top: rect.bottom + scrollTop + 4, // 4px gap (mt-1)
        left: rect.left + scrollLeft,
        width: rect.width,
      });
    }
  }, [open]);

  // Handle window resize and scroll
  useEffect(() => {
    if (!open) return;

    function updatePosition() {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft =
          window.pageXOffset || document.documentElement.scrollLeft;

        setDropdownPosition({
          top: rect.bottom + scrollTop + 4,
          left: rect.left + scrollLeft,
          width: rect.width,
        });
      }
    }

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setHighlighted(-1);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [open]);

  // Get user role from localStorage
  let detectedRole = "admin";
  if (typeof window !== "undefined") {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const userObj = JSON.parse(userStr);
        detectedRole = userObj?.user_type === "client" ? "client" : "admin";
      }
    } catch {}
  }

  // Determine border and focus color based on role
  const borderColor = errorQuery ? "border-red-500" : "focus:ring-primary";

  function handleKeyDown(
    e: React.KeyboardEvent<HTMLButtonElement | HTMLDivElement>,
  ) {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        setOpen(true);
        setHighlighted(0);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      setHighlighted((prev) => Math.min(prev + 1, filteredOptions.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setHighlighted((prev) => Math.max(prev - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter" && highlighted >= 0) {
      if (filteredOptions[highlighted]) {
        handleSelect(filteredOptions[highlighted]);
      }
      e.preventDefault();
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlighted(-1);
      e.preventDefault();
    }
  }

  function handleSelect(opt: CSelectOption) {
    onChange?.(opt.value);
    setOpen(false);
    setHighlighted(-1);
    setSearch("");
  }

  return (
    <div className={`relative ${className}`} style={style}>
      {label && (
        <label
          htmlFor={inputId}
          className="text-sm text-muted-foreground mb-1 block"
        >
          {label}
        </label>
      )}
      <div className={`relative ${width || "w-full"}`}>
        {/* Button */}
        <button
          id={inputId}
          ref={buttonRef}
          type="button"
          className={`w-full border rounded px-4 py-2 pr-10 bg-background text-left focus:ring-2 focus:border-transparent text-sm transition-colors h-10.5 border-primary/10
            dark:border-primary/30
            ${borderColor}
            ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open ? "true" : "false"}
          onClick={() => !disabled && setOpen((v) => !v)}
          onKeyDown={handleKeyDown}
        >
          <span
            className={
              selectedOption
                ? "text-foreground truncate block w-full pr-2"
                : "text-muted-foreground truncate block w-full pr-2"
            }
            style={{ maxWidth: "calc(100% - 2.5rem)" }}
          >
            {selectedOption && selectedOption.value !== ""
              ? (selectedOption.selectedLabel ?? selectedOption.label)
              : (placeholder ?? `${required ? "*" : ""} Select an option`)}
          </span>
          {/* Clear button */}
          {clearable &&
            selectedOption &&
            selectedOption.value !== "" &&
            !disabled && (
              <span
                className="absolute right-8 top-1/2 -translate-y-1/2 flex items-center text-muted-foreground cursor-pointer hover:text-destructive z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.("");
                  setOpen(false);
                  setHighlighted(-1);
                  setSearch("");
                }}
                title="Clear selection"
                tabIndex={-1}
              >
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </span>
            )}
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 flex items-center text-muted-foreground">
            <svg
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </span>
        </button>

        {/* Portal-based Dropdown */}
        {mounted &&
          createPortal(
            <AnimatePresence>
              {open && (
                <motion.div
                  className="fixed"
                  ref={dropdownRef}
                  tabIndex={-1}
                  onKeyDown={handleKeyDown}
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`,
                    minWidth: "150px",
                    zIndex: 1000000,
                  }}
                  initial={{ opacity: 0, scale: 0.98, y: -8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98, y: -8 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                >
                  {/* Search bar at top */}
                  {searchable && (
                    <div className="px-2 pt-2 pb-1 border-b border-border bg-background rounded-t-lg">
                      <input
                        type="text"
                        className="w-full px-2 py-1 rounded border border-border text-sm bg-background focus:outline-none"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setHighlighted(0);
                        }}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "ArrowDown") {
                            setHighlighted((prev) =>
                              Math.min(prev + 1, filteredOptions.length - 1),
                            );
                            e.preventDefault();
                          } else if (e.key === "ArrowUp") {
                            setHighlighted((prev) => Math.max(prev - 1, 0));
                            e.preventDefault();
                          } else if (e.key === "Enter" && highlighted >= 0) {
                            if (filteredOptions[highlighted]) {
                              handleSelect(filteredOptions[highlighted]);
                            }
                            e.preventDefault();
                          } else if (e.key === "Escape") {
                            setOpen(false);
                            setHighlighted(-1);
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                  )}

                  {/* Options */}
                  <div
                    className={`w-full ${maxHeight} overflow-auto ${
                      searchable ? "rounded-b-lg" : "rounded"
                    } bg-background py-1 text-base shadow-sm ring-1 ring-border ring-opacity-5 focus:outline-none sm:text-sm`}
                    role="listbox"
                  >
                    {filteredOptions.length === 0 && (
                      <div className="text-muted-foreground px-4 py-2">
                        No options
                      </div>
                    )}
                    {filteredOptions.map((opt, idx) => {
                      const isSelected =
                        selectedOption && selectedOption.value === opt.value;
                      const isActive = idx === highlighted;
                      const isDisabled = opt.disabled || false;

                      return (
                        <div
                          key={opt.value}
                          role="option"
                          aria-selected={isSelected ? "true" : "false"}
                          aria-disabled={isDisabled ? "true" : "false"}
                          className={`select-none relative py-2 pl-4 pr-10 transition-colors
                            ${
                              isDisabled
                                ? "cursor-not-allowed opacity-50 text-muted-foreground bg-secondary"
                                : `cursor-pointer ${
                                    isActive
                                      ? "bg-primary/10 text-primary"
                                      : "text-foreground hover:bg-accent"
                                  }`
                            }
                            ${
                              isSelected && !isDisabled
                                ? "font-semibold"
                                : "font-normal"
                            }`}
                          onMouseEnter={() =>
                            !isDisabled && setHighlighted(idx)
                          }
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => !isDisabled && handleSelect(opt)}
                        >
                          <span
                            className={`block pr-8 ${
                              typeof opt.label === "string" ? "truncate" : ""
                            } ${
                              isSelected && !isDisabled
                                ? "font-semibold"
                                : "font-normal"
                            }`}
                            style={
                              typeof opt.label === "string"
                                ? { maxWidth: "calc(100% - 2rem)" }
                                : undefined
                            }
                            title={
                              typeof opt.label === "string" ? opt.label : ""
                            }
                          >
                            {opt.label}
                            {isDisabled && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                (Disabled)
                              </span>
                            )}
                          </span>
                          {isSelected && !isDisabled && (
                            <span className="absolute inset-y-0 right-3 flex items-center text-primary">
                              <svg
                                width="18"
                                height="18"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="3"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>,
            document.body,
          )}
      </div>
      {/* {errorQuery && (
        <p className="text-red-500 text-xs mt-1">{errorMessage}</p>
      )} */}
    </div>
  );
};

export default CSelect;
